import { create } from 'zustand';

import type { ApiClient, TokenSet } from '../api/client';
import type {
  AuthenticationResponse,
  Permission,
  PrincipalResponse,
  SupportSessionResponse,
} from '../api/types';
import { loadSupportSession, saveSupportSession } from './sessionPersistence';

/**
 * Authentication state for the whole dashboard.
 *
 * A phase rather than a boolean, because "signing in" is a state the sign-in form has to render
 * differently from "not signed in" — the submit button and the error slot both depend on it.
 *
 * Tokens live in the {@link ApiClient}, not here: the store holds who the user is and what they
 * may do, the client holds how to prove it. Keeping them apart means nothing that renders can
 * accidentally put a token on screen or in a log.
 */

export type AuthPhase = 'anonymous' | 'authenticating' | 'authenticated';

/**
 * A live support grant this operator is currently working inside (SEC-003).
 *
 * Held separately from {@link AuthState.activeClinicId} because it is not the same kind of thing:
 * an active clinic is where a staff member works, a support session is a borrowed, expiring,
 * read-only view into somebody else's clinic. Conflating them would make the banner that says so
 * impossible to render correctly, and that banner is most of the point.
 */
export interface SupportSession {
  readonly clinicId: string;
  readonly grantId: string;
  /** When the grant ends — not when the token does. The clinic's deadline is the real one. */
  readonly grantExpiresAt: string | null;
  readonly clinicLabel: string;
}

export interface AuthState {
  phase: AuthPhase;
  principal: PrincipalResponse | null;
  permissions: ReadonlySet<Permission>;
  activeClinicId: string | null;
  supportSession: SupportSession | null;
  error: unknown;

  signIn: (client: ApiClient, email: string, password: string) => Promise<AuthPhase>;
  /** After a reload: the client still has tokens, so ask the server whose they are. */
  restore: (client: ApiClient) => Promise<void>;
  selectClinic: (client: ApiClient, clinicId: string) => Promise<void>;
  /** Trades a grant for a read-only token bound to that one clinic. */
  openSupportSession: (client: ApiClient, clinicId: string, clinicLabel: string) => Promise<void>;
  /** Drops the borrowed token and returns to the operator's own platform scope. */
  endSupportSession: (client: ApiClient) => Promise<void>;
  signOut: (client: ApiClient) => Promise<void>;
  /** Clears local state after the API says the session is gone. */
  sessionExpired: () => void;
}

function permissionsOf(principal: PrincipalResponse | null): ReadonlySet<Permission> {
  return new Set(principal?.permissions ?? []);
}

function tokensFrom(response: AuthenticationResponse): TokenSet | null {
  if (!response.accessToken || !response.refreshToken) {
    return null;
  }
  return {
    accessToken: response.accessToken,
    refreshToken: response.refreshToken,
    ...(response.accessTokenExpiresAt ? { accessTokenExpiresAt: response.accessTokenExpiresAt } : {}),
    ...(response.refreshTokenExpiresAt ? { refreshTokenExpiresAt: response.refreshTokenExpiresAt } : {}),
  };
}

const ANONYMOUS = {
  phase: 'anonymous' as AuthPhase,
  principal: null,
  permissions: new Set<Permission>() as ReadonlySet<Permission>,
  activeClinicId: null,
  supportSession: null,
  error: null,
};

export const useAuthStore = create<AuthState>((set) => ({
  ...ANONYMOUS,

  async signIn(client, email, password) {
    set({ phase: 'authenticating', error: null });
    try {
      const response = await client.post<AuthenticationResponse>(
        '/auth/staff/sign-in',
        { email, password, deviceLabel: describeDevice() },
        { anonymous: true },
      );
      return applyOutcome(set, client, response);
    } catch (error) {
      set({ phase: 'anonymous', error });
      throw error;
    }
  },

  async restore(client) {
    if (!client.isAuthenticated) {
      return;
    }
    try {
      // Who the user is and what they may do is asked again rather than remembered: a role or a
      // clinic can change between two loads of the page, and the server is the one that knows.
      const principal = await client.get<PrincipalResponse>('/me');
      // A borrowed token that expired came back from the refresh as the operator's own, so the
      // banner returns only while the token still points at the clinic it names.
      const stored = loadSupportSession();
      const supportSession = stored && stored.clinicId === principal.activeClinicId ? stored : null;
      if (!supportSession) {
        // The state was empty already, so the mirror below sees no change and would leave it.
        saveSupportSession(null);
      }
      set({
        phase: 'authenticated',
        principal,
        permissions: permissionsOf(principal),
        activeClinicId: principal.activeClinicId ?? null,
        supportSession,
        error: null,
      });
    } catch {
      // Whatever the reason, a session that cannot be confirmed is not one to keep.
      client.setTokens(null);
      set({ ...ANONYMOUS });
    }
  },

  async selectClinic(client, clinicId) {
    const response = await client.post<{ accessToken?: string; activeClinicId?: string }>(
      '/me/active-clinic',
      { clinicId },
    );
    const tokens = client.getTokens();
    if (response.accessToken && tokens) {
      client.setTokens({ ...tokens, accessToken: response.accessToken });
    }
    // Permissions differ per clinic, so the principal is re-read rather than assumed.
    const principal = await client.get<PrincipalResponse>('/me');
    set({
      principal,
      permissions: permissionsOf(principal),
      activeClinicId: principal.activeClinicId ?? clinicId,
    });
  },

  async openSupportSession(client, clinicId, clinicLabel) {
    const session = await client.post<SupportSessionResponse>('/platform/support/sessions', {
      clinicId,
    });
    const tokens = client.getTokens();
    if (!session.accessToken || !tokens) {
      throw new Error('The support session returned no token');
    }
    // Only the access token is swapped. The refresh token still belongs to the operator's own
    // platform session, which is what makes ending the support session a refresh rather than a
    // second sign-in.
    client.setTokens({ ...tokens, accessToken: session.accessToken });

    // Permissions come from the server's fixed support scope, never from anything assembled here —
    // a client that decided its own permissions would be a client that could widen them.
    const principal = await client.get<PrincipalResponse>('/me');
    set({
      principal,
      permissions: permissionsOf(principal),
      activeClinicId: session.clinicId ?? clinicId,
      supportSession: {
        clinicId: session.clinicId ?? clinicId,
        grantId: session.grantId ?? '',
        grantExpiresAt: session.grantExpiresAt ?? null,
        clinicLabel,
      },
      error: null,
    });
  },

  async endSupportSession(client) {
    const refreshToken = client.getTokens()?.refreshToken;
    if (!refreshToken) {
      // Nothing to go back to; treat it as a sign-out rather than leaving a borrowed token live.
      client.setTokens(null);
      set({ ...ANONYMOUS });
      return;
    }
    const refreshed = await client.post<AuthenticationResponse>(
      '/auth/token/refresh',
      { refreshToken },
      { anonymous: true },
    );
    const tokens = tokensFrom(refreshed);
    if (tokens) {
      client.setTokens(tokens);
    }
    const principal = await client.get<PrincipalResponse>('/me');
    set({
      principal,
      permissions: permissionsOf(principal),
      activeClinicId: principal.activeClinicId ?? null,
      supportSession: null,
      error: null,
    });
  },

  async signOut(client) {
    const refreshToken = client.getTokens()?.refreshToken;
    try {
      if (refreshToken) {
        await client.post<void>('/auth/sign-out', { refreshToken }, { anonymous: true });
      }
    } finally {
      // Local state is cleared whether or not the server acknowledged: the user asked to leave.
      client.setTokens(null);
      set({ ...ANONYMOUS });
    }
  },

  sessionExpired() {
    set({ ...ANONYMOUS });
  },
}));

// The support session is mirrored here rather than saved at each call site, so no path that opens,
// ends or drops one can forget to, and a reload never shows a banner for a session already over.
useAuthStore.subscribe((state, previous) => {
  if (state.supportSession !== previous.supportSession) {
    saveSupportSession(state.supportSession);
  }
});

type SetState = (partial: Partial<AuthState>) => void;

function applyOutcome(set: SetState, client: ApiClient, response: AuthenticationResponse): AuthPhase {
  const tokens = tokensFrom(response);
  if (tokens) {
    client.setTokens(tokens);
  }
  const principal = response.principal ?? null;
  set({
    phase: 'authenticated',
    principal,
    permissions: permissionsOf(principal),
    activeClinicId: response.activeClinicId ?? principal?.activeClinicId ?? null,
    supportSession: null,
    error: null,
  });
  return 'authenticated';
}

/** A short, non-identifying label so staff can recognise their own sessions. */
function describeDevice(): string {
  const agent = globalThis.navigator?.userAgent ?? '';
  const browser = /Edg\//.test(agent)
    ? 'Edge'
    : /Chrome\//.test(agent)
      ? 'Chrome'
      : /Firefox\//.test(agent)
        ? 'Firefox'
        : /Safari\//.test(agent)
          ? 'Safari'
          : 'Browser';
  const platform = /Windows/.test(agent)
    ? 'Windows'
    : /Mac OS/.test(agent)
      ? 'macOS'
      : /Android/.test(agent)
        ? 'Android'
        : /iPhone|iPad/.test(agent)
          ? 'iOS'
          : 'Unknown';
  return `${browser} on ${platform}`;
}
